<template>
  <div class="container-nzidoo">
    <page-title title="sidebar.paymentTypesManagement" />

    <!-- Toolbar -->
    <div class="admin-toolbar">
      <div class="admin-search-box">
        <i class="mdi mdi-magnify admin-search-icon"></i>

        <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')" class="admin-search-input">

        <!-- Clear icon -->
        <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
          :title="$t('sentence.clear')"></i>
      </div>
      <button class="btn btn-primary" @click="openCreateModal">
        <i class="mdi mdi-plus"></i>
        <div class="d-none d-md-block">
          {{ $t("action.add") }}
        </div>
      </button>
    </div>

    <LoadingSpinner v-if="loader" />
    <!-- Payment Types Grid -->
    <div class="admin-grid">
      <div v-for="paymentType in filteredPaymentTypes" :key="paymentType.id" class="admin-card">
        <div class="admin-card-header">
          <div class="payment-header-left">
            <img v-if="paymentType.icon" :src="`${config.baseurl}${paymentType.icon}`" :alt="paymentType.name" class="payment-icon" />
            <span class="payment-card-code">{{ paymentType.name }}</span>
          </div>
          <div class="admin-card-actions">
            <button class="btn btn-icon-action btn-warning" @click="edit(paymentType.id)" :title="$t('action.update')">
              <i class="mdi mdi-pencil"></i>
            </button>
            <button class="btn btn-icon-action btn-danger" @click="remove(paymentType.id)" :title="$t('action.delete')">
              <i class="mdi mdi-delete"></i>
            </button>
          </div>
        </div>
        <div class="admin-card-body">
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t('attr.name') }}</label>
            <div class="card-name">{{ paymentType.name }}</div>
          </div>
          <div class="admin-card-section" v-if="paymentType.description">
            <label class="admin-card-label">{{ $t('attr.description') }}</label>
            <div class="card-light">{{ paymentType.description }}</div>
          </div>
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t('main.status') }}</label>
            <span :class="['status-badge', paymentType.is_active ? 'status-active' : 'status-inactive']">
              {{ paymentType.is_active ? $t('status.active') : $t('status.inactive') }}
            </span>
          </div>
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t('attr.sortOrder') }}</label>
            <div class="card-light">{{ paymentType.sort_order }}</div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredPaymentTypes.length === 0" class="admin-empty-state">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <p class="admin-empty-text">{{ $t('sentence.noResults') || 'Aucun résultat trouvé' }}</p>
      </div>
    </div>

    <!-- Drawer Backdrop -->
    <transition name="drawer-backdrop">
      <div v-if="showModal" class="admin-drawer-backdrop" @click="closeModal"></div>
    </transition>

    <!-- Drawer -->
    <transition name="drawer">
      <div v-if="showModal" class="admin-drawer-container">
        <div class="admin-drawer-header">
          <h3 class="admin-drawer-title">
            {{ isEditing ? $t('action.update') : $t('action.add') }} {{ $t('main.paymentType') }}
          </h3>
          <button class="btn btn-close" @click="closeModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="admin-drawer-body">
          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('attr.name') }} *</label>
            <input type="text" v-model="form.name" class="admin-form-input" :placeholder="$t('attr.name')">
          </div>

          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('attr.code') }} *</label>
            <input type="text" v-model="form.code" class="admin-form-input" :placeholder="$t('attr.code')"
              :disabled="isEditing">
          </div>

          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('attr.icon') }}</label>
            <input type="text" v-model="form.icon" class="admin-form-input" placeholder="mdi mdi-credit-card">
            <small class="form-help-text">{{ $t('sentence.iconHelp') || 'Ex: mdi mdi-credit-card, mdi mdi-cash'
              }}</small>
          </div>

          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('attr.description') }}</label>
            <textarea v-model="form.description" class="admin-form-textarea" rows="4"
              :placeholder="$t('attr.description')"></textarea>
          </div>

          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('attr.sortOrder') }}</label>
            <input type="number" v-model.number="form.sort_order" class="admin-form-input"
              :placeholder="$t('attr.sortOrder')" min="0">
          </div>

          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('main.status') }}</label>
            <div class="admin-form-checkbox">
              <input type="checkbox" v-model="form.is_active" id="is_active">
              <label for="is_active">{{ $t('status.active') }}</label>
            </div>
          </div>
        </div>

        <div class="admin-drawer-footer">
          <button class="btn btn-secondary" @click="closeModal">
            {{ $t('action.cancel') }}          </button>
          <button class="btn btn-primary" @click="save">
            {{ isEditing ? $t('action.update') : $t('action.create') }}
          </button>
        </div>
      </div>
    </transition>

    <notifications style="top:5%;" width="35%" group="error" position="top center" />
    <notifications style="top:5%;" width="35%" group="success" position="top center" />
  </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import AlertService from '@/services/alertService';

import config from "@/config";

export default {
  components: { PageTitle,LoadingSpinner },
  name: "PaymentTypeAdmin",

  data() {
    return {
      paymentTypes: [],
      showModal: false,
      isEditing: false,
      config: config,
      editId: null,
      searchTerm: "",
      loader: false,
      form: {
        name: "",
        code: "",
        icon: "",
        description: "",
        is_active: true,
        sort_order: 0
      }
    };
  },

  computed: {
    filteredPaymentTypes() {
      if (!this.searchTerm) return this.paymentTypes;

      const term=this.searchTerm.toLowerCase();
      return this.paymentTypes.filter(paymentType =>
        (paymentType.name&&paymentType.name.toLowerCase().includes(term))||
        paymentType.code.toLowerCase().includes(term)||
        (paymentType.description&&paymentType.description.toLowerCase().includes(term))
      );
    }
  },

  mounted() {
    this.loadPaymentTypes();
  },

  methods: {
    clearSearch() {
      this.searchTerm="";
    },

    async loadPaymentTypes() {
      try {
        this.loader=true;
        const res=await this.$axios.get("/admin/payment-types");
        this.paymentTypes=Object.values(res.data.data);
        this.loader=false;
      } catch (err) {
        console.error("Error loading payment types:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorLoading')||"Erreur lors du chargement",
          type: "error",
        });
      }
    },

    openCreateModal() {
      this.isEditing=false;
      this.showModal=true;
      this.form={
        name: "",
        code: "",
        icon: "",
        description: "",
        is_active: true,
        sort_order: 0
      };
    },

    closeModal() {
      this.showModal=false;
    },

    async save() {
      if (!this.form.name||!this.form.name.trim()) {
        this.$notify({
          group: "error",
          title: this.$t('sentence.nameRequired')||"Le nom est requis",
          type: "error",
        });
        return;
      }

      if (!this.form.code||!this.form.code.trim()) {
        this.$notify({
          group: "error",
          title: this.$t('sentence.codeRequired')||"Le code est requis",
          type: "error",
        });
        return;
      }

      try {
        const formData=new FormData();
        formData.append("name", this.form.name);
        formData.append("code", this.form.code);
        formData.append("icon", this.form.icon||"");
        formData.append("description", this.form.description||"");
        formData.append("is_active", this.form.is_active? 1:0);
        formData.append("sort_order", this.form.sort_order||0);

        if (this.isEditing) {
          await this.$axios.post(`/admin/payment-types/update/${this.editId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          this.$notify({
            group: "success",
            title: this.$t('sentence.updateSuccess')||"Type de paiement mis à jour avec succès",
            type: "success",
          });
        } else {
          await this.$axios.post("/admin/payment-types/create", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          this.$notify({
            group: "success",
            title: this.$t('sentence.createSuccess')||"Type de paiement créé avec succès",
            type: "success",
          });
        }
        this.closeModal();
        this.loadPaymentTypes();
      } catch (err) {
        console.error("Error saving payment type:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorSaving')||"Erreur lors de l'enregistrement",
          type: "error",
        });
      }
    },

    async edit(id) {
      this.isEditing=true;
      this.editId=id;

      try {
        const res=await this.$axios.get(`/admin/payment-types/${id}`);
        this.form={
          name: res.data.data.name||"",
          code: res.data.data.code,
          icon: res.data.data.icon||"",
          description: res.data.data.description||"",
          is_active: res.data.data.is_active,
          sort_order: res.data.data.sort_order||0
        };
        this.showModal=true;
      } catch (err) {
        console.error("Error loading payment type:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorLoading')||"Erreur lors du chargement",
          type: "error",
        });
      }
    },

    async remove(id) {
      const confirmed=await AlertService.confirm({
        title: this.$t('sentence.confirmDelete')||"Êtes-vous sûr ?",
        confirmButtonText: this.$t('action.delete')||"Supprimer",
        cancelButtonText: this.$t('action.cancel')||"Annuler",
        icon: "warning"
      });

      if (!confirmed) return;

      try {
        await this.$axios.post(`/admin/payment-types/delete/${id}`);
        this.$notify({
          group: "success",
          title: this.$t('sentence.deleteSuccess')||"Type de paiement supprimé avec succès",
          type: "success",
        });
        this.loadPaymentTypes();
      } catch (err) {
        console.error("Error deleting payment type:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorDeleting')||"Erreur lors de la suppression",
          type: "error",
        });
      }
    }
  }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Component-specific styles */
.payment-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* .payment-icon {
  font-size: 20px;
  color: var(--primary-color, #3b82f6);
} */

.payment-card-code {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: var(--gray-100);
  padding: 4px 10px;
  border-radius: 6px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background-color: #d1fae5;
  color: #065f46;
}

.status-inactive {
  background-color: #fee2e2;
  color: #991b1b;
}

.admin-form-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
}

.admin-form-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.admin-form-checkbox label {
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.form-help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: var(--gray-500);
  font-style: italic;
}

@media (max-width: 768px) {
  .admin-drawer-container {
    max-width: 100%;
  }

  .payment-header-left {
    gap: 6px;
  }

  .payment-icon {
    font-size: 18px;
  }
}
</style>